import React, { useState, useRef } from 'react';
import './App.css';

function App() {
  const [selectedType, setSelectedType] = useState('recon');
  const [target, setTarget] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const inputRef = useRef(null);

  const tools = [
    { id: 'recon', name: 'Reconnaissance', emoji: '🔍', desc: 'Information Gathering' },
    { id: 'web', name: 'Web Scanner', emoji: '🌐', desc: 'Vulnerability Analysis' },
    { id: 'crypto', name: 'Crypto Solver', emoji: '🔐', desc: 'Decryption Engine' },
    { id: 'forensics', name: 'Forensics', emoji: '🔬', desc: 'Digital Analysis' },
  ];

  const scenarios = [
    { id: 1, name: 'SQL Injection Attack', type: 'web', target: 'example.com/api?id=1', difficulty: 'Medium' },
    { id: 2, name: 'Caesar Cipher', type: 'crypto', target: 'KHOOR ZRUOG', difficulty: 'Easy' },
    { id: 3, name: 'Domain Reconnaissance', type: 'recon', target: 'github.com', difficulty: 'Easy' },
    { id: 4, name: 'PDF Steganography', type: 'forensics', target: 'document.pdf', difficulty: 'Hard' },
  ];

  const getMockResults = (scanType, targetValue) => {
    const resultMap = {
      recon: [
        { step: 1, tool: 'DNS Resolver', action: 'Resolve Domain', status: 'success', output: `${targetValue} → 192.168.1.100` },
        { step: 2, tool: 'WHOIS Lookup', action: 'Get Registration Info', status: 'success', output: 'Registered: 2020-01-15 | Registrar: GoDaddy' },
        { step: 3, tool: 'SSL Certificate', action: 'Check SSL/TLS', status: 'success', output: 'Valid Certificate | Expires: 2026-01-15' },
        { step: 4, tool: 'Port Scanner', action: 'Scan Open Ports', status: 'success', output: 'Open: 80, 443, 8080 | Closed: 22, 3306' },
        { step: 5, tool: 'Service Detection', action: 'Identify Services', status: 'success', output: 'Apache 2.4.41 | OpenSSH 7.4 | MySQL 5.7' },
      ],
      web: [
        { step: 1, tool: 'URL Parser', action: 'Analyze Endpoint', status: 'success', output: `Target: ${targetValue}` },
        { step: 2, tool: 'Parameter Fuzzer', action: 'Discover Parameters', status: 'success', output: 'Found: id, user, email, role' },
        { step: 3, tool: 'Injection Tester', action: 'Test SQL Injection', status: 'critical', output: 'VULNERABLE: SQL Injection Detected' },
        { step: 4, tool: 'Payload Generator', action: 'Create Exploit', status: 'success', output: "Payload: admin' OR '1'='1'--" },
        { step: 5, tool: 'Execution Engine', action: 'Execute Exploit', status: 'success', output: 'Success: Database accessed | 1,245 records extracted' },
      ],
      crypto: [
        { step: 1, tool: 'Cipher Detector', action: 'Identify Cipher Type', status: 'success', output: `Analysis: ${targetValue}` },
        { step: 2, tool: 'Frequency Analysis', action: 'Analyze Patterns', status: 'success', output: 'Pattern Match: Caesar cipher with shift 3' },
        { step: 3, tool: 'Brute Force', action: 'Try All Shifts', status: 'success', output: 'Shift 3 matched: HELLO WORLD' },
        { step: 4, tool: 'Validation', action: 'Verify Decryption', status: 'success', output: 'Valid English detected | Confidence: 99.8%' },
        { step: 5, tool: 'Flag Extraction', action: 'Extract Flag', status: 'success', output: 'Flag: CTF{CAESAR_DECODED}' },
      ],
      forensics: [
        { step: 1, tool: 'File Analyzer', action: 'Examine File', status: 'success', output: `${targetValue} | Size: 2.5MB | Type: PDF` },
        { step: 2, tool: 'Metadata Parser', action: 'Extract Metadata', status: 'success', output: 'Author: John Doe | Created: 2023-06-15 | Modified: 2023-07-20' },
        { step: 3, tool: 'Steganography Detector', action: 'Scan for Hidden Data', status: 'warning', output: 'Hidden content detected in PDF streams' },
        { step: 4, tool: 'Data Extractor', action: 'Extract Hidden Content', status: 'success', output: 'Extracted: secret_message.txt (1.2KB)' },
        { step: 5, tool: 'Decryption Engine', action: 'Decrypt Content', status: 'success', output: 'Flag: CTF{FORENSICS_COMPLETE}' },
      ]
    };

    return resultMap[scanType] || resultMap.recon;
  };

  const handleScan = async () => {
    if (!target.trim()) {
      inputRef.current?.focus();
      return;
    }

    setLoading(true);
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    setResults({
      type: selectedType,
      target: target,
      results: getMockResults(selectedType, target),
      flagFound: `CTF{${selectedType.toUpperCase()}_CAPTURED}`,
      timestamp: new Date().toISOString()
    });
    
    setLoading(false);
    setTimeout(() => {
      document.querySelector('.results-panel')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const handleScenarioSelect = (scenario) => {
    setTarget(scenario.target);
    setSelectedType(scenario.type);
    setResults(null);
    inputRef.current?.focus();
  };

  const copyFlag = () => {
    if (results?.flagFound) {
      navigator.clipboard.writeText(results.flagFound);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const exportResults = () => {
    if (!results) return;
    const data = JSON.stringify(results, null, 2);
    const element = document.createElement('a');
    element.setAttribute('href', 'data:text/plain;charset=utf-8,' + encodeURIComponent(data));
    element.setAttribute('download', `ctf-results-${Date.now()}.json`);
    element.style.display = 'none';
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  return (
    <div className="app">
      {/* HEADER */}
      <header className="app-header">
        <div className="header-content">
          <div className="header-logo">
            <span className="logo-icon">⚔️</span>
            <div className="logo-text">
              <h1>CTF Automation</h1>
              <p>Professional Challenge Solver</p>
            </div>
          </div>
          <div className="header-badge online">
            <span className="badge-dot"></span>
            <span>GitHub Pages</span>
          </div>
        </div>
      </header>

      {/* MAIN CONTENT */}
      <main className="app-main">
        <div className="content-wrapper">
          
          {/* SCANNER PANEL */}
          <div className="main-grid">
            <section className="scanner-panel">
              <div className="panel-header">
                <h2>Scanner Console</h2>
                <span className="panel-tag">Interactive</span>
              </div>

              {/* Tool Selector */}
              <div className="tool-selector">
                <label className="selector-label">SELECT TOOL</label>
                <div className="tool-grid">
                  {tools.map(tool => (
                    <button
                      key={tool.id}
                      className={`tool-card ${selectedType === tool.id ? 'active' : ''}`}
                      onClick={() => setSelectedType(tool.id)}
                      title={tool.desc}
                    >
                      <span className="tool-emoji">{tool.emoji}</span>
                      <span className="tool-title">{tool.name}</span>
                      <span className="tool-desc">{tool.desc}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Input Section */}
              <div className="input-section">
                <label className="input-label">TARGET INPUT</label>
                <div className="input-wrapper">
                  <span className="input-prefix">→</span>
                  <input
                    ref={inputRef}
                    type="text"
                    value={target}
                    onChange={(e) => setTarget(e.target.value)}
                    placeholder="Enter domain, URL, or data to analyze..."
                    className="target-input"
                    onKeyPress={(e) => e.key === 'Enter' && handleScan()}
                  />
                </div>
              </div>

              {/* Action Button */}
              <button
                onClick={handleScan}
                disabled={loading || !target.trim()}
                className={`scan-button ${loading ? 'loading' : ''}`}
                title="Press Enter or click to start analysis"
              >
                {loading ? (
                  <>
                    <span className="spinner"></span>
                    SCANNING...
                  </>
                ) : (
                  <>
                    <span className="button-icon">▶</span>
                    START ANALYSIS
                  </>
                )}
              </button>

              {/* Results Panel */}
              {results && (
                <div className="results-panel">
                  <div className="results-header">
                    <h3>ANALYSIS COMPLETE</h3>
                    <span className="results-target">Target: {results.target}</span>
                  </div>

                  {results.flagFound && (
                    <div className="flag-banner">
                      <div className="flag-icon">🚩</div>
                      <div className="flag-info">
                        <div className="flag-label">FLAG CAPTURED</div>
                        <div className="flag-value">{results.flagFound}</div>
                      </div>
                      <button 
                        className={`flag-copy-btn ${copied ? 'copied' : ''}`}
                        onClick={copyFlag}
                        title="Copy flag to clipboard"
                      >
                        {copied ? '✓ COPIED' : '📋 COPY'}
                      </button>
                    </div>
                  )}

                  <div className="steps-list">
                    {results.results.map((step) => (
                      <div key={step.step} className={`step-item status-${step.status}`} style={{ animationDelay: `${step.step * 0.1}s` }}>
                        <div className="step-number">{step.step}</div>
                        <div className="step-details">
                          <div className="step-tool">
                            <strong>{step.tool}</strong>
                            <span className={`status-badge ${step.status}`}>{step.status}</span>
                          </div>
                          <div className="step-action">{step.action}</div>
                          <div className="step-output">{step.output}</div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="results-actions">
                    <button onClick={exportResults} className="export-btn">
                      📥 EXPORT JSON
                    </button>
                  </div>
                </div>
              )}
            </section>

            {/* SCENARIOS PANEL */}
            <aside className="scenarios-panel">
              <div className="panel-header">
                <h2>Scenarios</h2>
                <span className="panel-tag">Library</span>
              </div>

              <div className="scenarios-list">
                {scenarios.map(scenario => (
                  <button
                    key={scenario.id}
                    className="scenario-item"
                    onClick={() => handleScenarioSelect(scenario)}
                    title={`Load: ${scenario.name}`}
                  >
                    <div className="scenario-header">
                      <h4>{scenario.name}</h4>
                      <span className={`difficulty ${scenario.difficulty.toLowerCase()}`}>
                        {scenario.difficulty}
                      </span>
                    </div>
                    <div className="scenario-meta">
                      <span className="scenario-type">{scenario.type}</span>
                      <span className="scenario-target">{scenario.target}</span>
                    </div>
                  </button>
                ))}
              </div>
            </aside>
          </div>
        </div>
      </main>

      {/* FOOTER */}
      <footer className="app-footer">
        <div className="footer-content">
          <div className="footer-section">
            <h4>ABOUT</h4>
            <p>Professional CTF automation platform running 100% client-side on GitHub Pages</p>
          </div>
          <div className="footer-section">
            <h4>FEATURES</h4>
            <ul>
              <li>Real-time vulnerability analysis</li>
              <li>Cryptography solver</li>
              <li>Forensics examination</li>
              <li>Multi-tool integration</li>
            </ul>
          </div>
          <div className="footer-section">
            <h4>KEYBOARD SHORTCUTS</h4>
            <ul>
              <li>Enter - Start Analysis</li>
              <li>Tab - Navigate Tools</li>
              <li>1-4 - Select Tool</li>
            </ul>
          </div>
        </div>
        <div className="footer-bottom">
          <p>© 2024 CTF Automation Platform | Educational Use Only</p>
        </div>
      </footer>
    </div>
  );
}

export default App;
